﻿import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from 'recharts'
import { Link } from 'react-router-dom'
import { Heading, Text, LoadingSpinner, Input, Button, Select } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { qualityService, IncidentFilters } from '../../services/quality'
import { modulesService } from '../../services/modules'
import {
  QualityCategory,
  QualityParameter,
  QualityTicket as QualityIncident,
  QualityTicketAttachment,
  QualityTicketHistory as QualityIncidentHistory,
  QualityTicketOrigin as QualityIncidentOrigin,
  QualityTicketStage as QualityIncidentStage,
  QualityTicketType as QualityIncidentType,
  Severity,
  TicketKind,
  formatTicketNumber,
} from '../../types/quality'
import { buildDepartmentLabelMap, DEPARTMENT_RESP_LABEL, formatBrokerTicketLabel, formatProfilePickerLabel, formatRequesterDisplay, type QualityActiveProfile } from './qualidadeDisplay'
import { canUseNotApplicableKind, kindLabel } from './qualidadeKind'
import { hasActiveTicketFilters, buildDefaultDashFilters } from './qualidadeFilters'
import { useAuthContext } from '../../contexts/AuthContext'
import { TicketDetailDrawer as IncidentDetailDrawer } from './TicketDetailDrawer'
import { TicketEditModal as IncidentEditModal, isTicketFormValid } from './TicketEditModal'
import { CheckboxMultiSelect } from './CheckboxMultiSelect'
import {
  parseQualityBusinessDayWindow,
  getElapsedBusinessHoursMs,
  formatBusinessHours,
  classifySlaPrimeiraResposta,
  classifyMaxDeadline,
  formatElapsedCalendarTime,
  parseSlaPrimeiraRespostaGroupIds,
  type SlaFirstResponseStatus,
  type MaxDeadlineStatus,
} from './qualityBusinessHours'

const MODULE_NAME = 'qualidade_dashboard'
/** Permissão do botão Editar no drawer de detalhe (igual à página Incidentes). */
const INCIDENTES_MODULE_NAME = 'qualidade_tickets'

type Tab = 'sla' | 'max_deadline' | 'indicators' | 'kanban' | 'matrix'

const KIND_FILTER_OPTIONS: { value: TicketKind; label: string }[] = [
  { value: 'incident', label: 'Incidente' },
  { value: 'improvement', label: 'Melhoria' },
  { value: 'suggestion', label: 'Sugestão' },
  { value: 'not_applicable', label: 'Não aplicável' },
]

const SEVERITY_FILTER_OPTIONS: { value: Severity; label: string }[] = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
]

const DELETED_FILTER_OPTIONS: { value: 'active' | 'deleted'; label: string }[] = [
  { value: 'active', label: 'Não excluídos' },
  { value: 'deleted', label: 'Excluídos' },
]

const SEVERITY_LABELS: Record<Severity, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
}

const SEVERITY_COLORS: Record<Severity, string> = {
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

const STAGE_COLORS_FALLBACK: Record<string, { container: string; border: string; headerText: string; accent: string }> = {
  open: {
    container: 'bg-primary-50 dark:bg-primary-900/20',
    border: 'border-primary-200 dark:border-primary-800',
    headerText: 'text-primary-700 dark:text-primary-300',
    accent: 'bg-primary-300 dark:bg-primary-600',
  },
  in_analysis: {
    container: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    headerText: 'text-yellow-700 dark:text-yellow-300',
    accent: 'bg-yellow-300 dark:bg-yellow-600',
  },
  in_treatment: {
    container: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    headerText: 'text-purple-700 dark:text-purple-300',
    accent: 'bg-purple-300 dark:bg-purple-600',
  },
  closed: {
    container: 'bg-gray-50 dark:bg-gray-900/30',
    border: 'border-gray-200 dark:border-gray-700',
    headerText: 'text-gray-600 dark:text-gray-400',
    accent: 'bg-gray-300 dark:bg-gray-600',
  },
}

// Converte um hex (#rrggbb) para rgba com opacidade
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}


const QualidadeDashboard: React.FC = () => {
  const [moduleTitle, setModuleTitle] = useState<string>('Dashboard de Qualidade')
  const [moduleDescription, setModuleDescription] = useState<string>('Indicadores, Kanban e Matriz')

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then(result => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const [activeTab, setActiveTab] = useState<Tab>('kanban')
  const [loading, setLoading] = useState(true)
  const [incidents, setIncidents] = useState<QualityIncident[]>([])
  const [stages, setStages] = useState<QualityIncidentStage[]>([])
  const [categories, setCategories] = useState<QualityCategory[]>([])
  const [departments, setDepartments] = useState<{ id: string; name: string; display_name: string }[]>([])
  const [activeProfiles, setActiveProfiles] = useState<QualityActiveProfile[]>([])
  const [incidentTypes, setIncidentTypes] = useState<QualityIncidentType[]>([])
  const [incidentOrigins, setIncidentOrigins] = useState<QualityIncidentOrigin[]>([])
  const [typeOriginLinks, setTypeOriginLinks] = useState<Map<string, string[]>>(new Map())
  const [byStage, setByStage] = useState<Record<string, QualityIncident[]>>({})
  const [globalRequesterUserId, setGlobalRequesterUserId] = useState<string>('')
  const [qualityParameters, setQualityParameters] = useState<QualityParameter[]>([])
  const [improcedentReasons, setImprocedentReasons] = useState<Awaited<ReturnType<typeof qualityService.listImprocedentReasons>>>([])
  const { profile } = useAuthContext()

  // Filtros compartilhados entre todas as abas (Kanban: últimos 30 dias por padrão)
  const [dashFilters, setDashFilters] = useState<IncidentFilters>(buildDefaultDashFilters)
  const [indicatorIncidents, setIndicatorIncidents] = useState<QualityIncident[]>([])
  const [filterLoading, setFilterLoading] = useState(false)
  /** ticket_id → ISO do 1º comentário ativo (para SLA da aba). */
  const [firstCommentAtByTicketId, setFirstCommentAtByTicketId] = useState<Record<string, string>>({})
  const [slaCommentsLoading, setSlaCommentsLoading] = useState(false)

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingIncident, setEditingIncident] = useState<Partial<QualityIncident> | null>(null)
  const [editSaving, setEditSaving] = useState(false)

  const [detailDrawerIncident, setDetailDrawerIncident] = useState<QualityIncident | null>(null)
  const [detailHistory, setDetailHistory] = useState<QualityIncidentHistory[]>([])
  const [detailHistoryLoading, setDetailHistoryLoading] = useState(false)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const departmentLabelMap = useMemo(() => buildDepartmentLabelMap(departments), [departments])

  const stageFilterOptions = useMemo(
    () => stages.map(s => ({ value: s.id, label: s.name })),
    [stages],
  )
  const categoryFilterOptions = useMemo(
    () => categories.map(c => ({ value: c.id, label: c.display_name })),
    [categories],
  )
  const departmentFilterOptions = useMemo(
    () => departments.map(d => ({ value: d.id, label: d.display_name })),
    [departments],
  )
  const requesterFilterOptions = useMemo(
    () => activeProfiles.map(p => ({
      value: p.id,
      label: formatProfilePickerLabel(p, departmentLabelMap),
    })),
    [activeProfiles, departmentLabelMap],
  )
  const ticketTypeFilterOptions = useMemo(
    () => incidentTypes.map(t => ({ value: t.id, label: t.display_name })),
    [incidentTypes],
  )
  const originFilterOptions = useMemo(
    () => incidentOrigins.map(o => ({ value: o.id, label: o.display_name })),
    [incidentOrigins],
  )

  const userDepartmentId = (profile as { department_id?: string | null } | null)?.department_id ?? null
  const canUseNotApplicable = useMemo(
    () => canUseNotApplicableKind(userDepartmentId, qualityParameters),
    [userDepartmentId, qualityParameters],
  )

  /** Corretores únicos presentes nos tickets (base total) — usados no filtro de indicadores. */
  const brokersInTickets = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; id_scgp: number | null }>()
    incidents.forEach(inc => {
      if (inc.broker_id && inc.broker) seen.set(inc.broker_id, inc.broker)
    })
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [incidents])

  const brokerFilterOptions = useMemo(
    () => brokersInTickets.map(b => ({ value: b.id, label: formatBrokerTicketLabel(b) })),
    [brokersInTickets],
  )

  const load = useCallback(async (requesterFilter?: string, incidentFilters?: IncidentFilters) => {
    setLoading(true)
    const filters: IncidentFilters = {
      ...(requesterFilter ? { requesterUserId: requesterFilter } : {}),
      ...(incidentFilters ?? {}),
    }
    const incidentQuery = hasActiveTicketFilters(filters) ? filters : undefined
    const [allIncidents, allStages, cats, deps, profs, types, origins, links, params, improcedent] = await Promise.all([
      qualityService.listIncidents(incidentQuery),
      qualityService.listStages(),
      qualityService.listCategoriesActive(),
      qualityService.listDepartmentsActive(),
      qualityService.listActiveProfilesForPicker(),
      qualityService.listIncidentTypesActive(),
      qualityService.listIncidentOriginsActive(),
      qualityService.listTicketTypeOriginLinks(),
      qualityService.listQualityParameters(),
      qualityService.listImprocedentReasons(),
    ])
    setIncidents(allIncidents)
    setIndicatorIncidents(allIncidents)
    setStages(allStages)
    setCategories(cats)
    setDepartments(deps)
    setActiveProfiles(profs)
    setIncidentTypes(types)
    setIncidentOrigins(origins)
    setTypeOriginLinks(links)
    setQualityParameters(params)
    setImprocedentReasons(improcedent)
    const dict: Record<string, QualityIncident[]> = {}
    allStages.forEach(s => { dict[s.id] = [] })
    allIncidents.forEach(inc => {
      if (dict[inc.stage_id]) {
        dict[inc.stage_id].push(inc)
      }
    })
    setByStage(dict)
    setLoading(false)
  }, [])

  const loadWithFilters = useCallback(async (f: IncidentFilters) => {
    setFilterLoading(true)
    const data = await qualityService.listIncidents(f)
    setIndicatorIncidents(data)
    setByStage(prev => {
      const dict: Record<string, QualityIncident[]> = {}
      Object.keys(prev).forEach(id => { dict[id] = [] })
      data.forEach(inc => {
        if (dict[inc.stage_id] !== undefined) dict[inc.stage_id].push(inc)
      })
      return dict
    })
    setFilterLoading(false)
  }, [])

  // Atualiza o mapa de 1º comentário qualificado sempre que tickets ou parâmetros mudam.
  useEffect(() => {
    const ids = indicatorIncidents.map(t => t.id)
    if (ids.length === 0) { setFirstCommentAtByTicketId({}); return }
    let cancelled = false
    setSlaCommentsLoading(true)
    const groupIds = parseSlaPrimeiraRespostaGroupIds(qualityParameters)
    qualityService.listFirstCommentAtByTicketIds(ids, groupIds).then(map => {
      if (!cancelled) {
        setFirstCommentAtByTicketId(Object.fromEntries(map))
        setSlaCommentsLoading(false)
      }
    })
    return () => { cancelled = true }
  }, [indicatorIncidents, qualityParameters])

  const applyDashFilter = (patch: Partial<IncidentFilters>) => {
    const next = { ...dashFilters, ...patch }
    setDashFilters(next)
    loadWithFilters(next)
  }

  const clearDashFilters = () => {
    setDashFilters({})
    loadWithFilters({})
  }

  const hasDashFilters = hasActiveTicketFilters(dashFilters)

  const handleRefresh = useCallback(async () => {
    await load(
      globalRequesterUserId || undefined,
      hasDashFilters ? dashFilters : undefined,
    )
  }, [load, globalRequesterUserId, dashFilters, hasDashFilters])

  const isRefreshing = loading || filterLoading

  const openKanbanIncidentDetail = useCallback(async (inc: QualityIncident) => {
    setDetailDrawerIncident(inc)
    setDetailHistory([])
    setDetailHistoryLoading(true)
    try {
      const [fresh, h] = await Promise.all([
        qualityService.getTicketById(inc.id),
        qualityService.listHistory(inc.id),
      ])
      if (fresh) setDetailDrawerIncident(fresh)
      setDetailHistory(h)
    } finally {
      setDetailHistoryLoading(false)
    }
  }, [])

  const closeDetailDrawer = useCallback(() => {
    setDetailDrawerIncident(null)
  }, [])

  const deleteDashboardIncident = useCallback(async (id: string) => {
    if (!window.confirm(
      'Excluir este ticket? A exclusão é lógica: some da lista e dos indicadores, ' +
      'mas o registro permanece com usuário e data da exclusão.'
    )) return
    try {
      await qualityService.deleteIncident(id)
      closeDetailDrawer()
      await load(
        globalRequesterUserId || undefined,
        hasDashFilters ? dashFilters : undefined,
      )
    } catch {
      alert('Não foi possível excluir este ticket.')
    }
  }, [closeDetailDrawer, load, globalRequesterUserId, hasDashFilters, dashFilters])

  const openKanbanIncidentEdit = useCallback((inc: QualityIncident) => {
    setEditingIncident({ ...inc })
    setEditModalOpen(true)
    setDetailDrawerIncident(null)
  }, [])

  const closeEditModal = useCallback(() => {
    setEditModalOpen(false)
    setEditingIncident(null)
  }, [])

  const saveDashboardIncident = useCallback(async (
    pendingFiles: File[] = [],
    toDelete: QualityTicketAttachment[] = []
  ) => {
    if (!editingIncident || !isTicketFormValid(editingIncident, {
      severityRequired: canUseNotApplicable,
      maxDeadlineEditable: canUseNotApplicable,
    })) return
    setEditSaving(true)
    try {
      const saved = await qualityService.upsertIncident(editingIncident)
      const ticketId = saved?.id ?? editingIncident.id
      if (ticketId) {
        await Promise.all(toDelete.map(att => qualityService.deleteTicketAttachment(att)))
        await Promise.all(pendingFiles.map(file => qualityService.uploadTicketAttachment(ticketId, file)))
      }
      closeEditModal()
      await load(
        globalRequesterUserId || undefined,
        hasDashFilters ? dashFilters : undefined,
      )
    } finally {
      setEditSaving(false)
    }
  }, [editingIncident, closeEditModal, load, globalRequesterUserId, canUseNotApplicable, hasDashFilters, dashFilters])

  useEffect(() => {
    load(globalRequesterUserId || undefined, buildDefaultDashFilters())
  }, [load, globalRequesterUserId])

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const [incId, fromStageId] = (active.id as string).split('|')
    const toStageId = over.id as string
    if (fromStageId === toStageId) return

    setByStage(prev => {
      const next = { ...prev }
      const item = next[fromStageId]?.find(i => i.id === incId)
      if (!item) return prev
      next[fromStageId] = (next[fromStageId] || []).filter(i => i.id !== incId)
      next[toStageId] = [{ ...item, stage_id: toStageId }, ...(next[toStageId] || [])]
      return next
    })

    try {
      await qualityService.updateIncidentStage(incId, toStageId)
    } catch {
      await load(
        globalRequesterUserId || undefined,
        hasDashFilters ? dashFilters : undefined,
      )
    }
  }

  const tabButtonClass = (active: boolean) =>
    `py-4 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  return (
    <div className="h-full flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <img
                    src="/selo-qualidade.png"
                    alt="Selo de qualidade"
                    className="h-14 w-auto flex-shrink-0 object-contain select-none"
                  />
                  <div className="min-w-0">
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">{moduleTitle}</Heading>
                    <Text size="sm" color="muted" className="mt-2">{moduleDescription}</Text>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center gap-2 shrink-0"
                  aria-label="Atualizar dashboard de qualidade"
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                    aria-hidden
                  />
                  Atualizar
                </Button>
              </div>
            </div>

            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button type="button" className={tabButtonClass(activeTab === 'kanban')} onClick={() => setActiveTab('kanban')}>
                  Kanban
                </button>
                <button type="button" className={tabButtonClass(activeTab === 'indicators')} onClick={() => setActiveTab('indicators')}>
                  Indicadores
                </button>
                <button type="button" className={tabButtonClass(activeTab === 'sla')} onClick={() => setActiveTab('sla')}>
                  SLA
                </button>
                <button type="button" className={tabButtonClass(activeTab === 'max_deadline')} onClick={() => setActiveTab('max_deadline')}>
                  Prazo Máximo
                </button>
                <button type="button" className={tabButtonClass(activeTab === 'matrix')} onClick={() => setActiveTab('matrix')}>
                  Matriz
                </button>
              </nav>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {/* Painel de filtros compartilhado (todas as abas) */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 overflow-visible">
                  <div className="flex items-center mb-3">
                    <FunnelIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                    <Text size="sm" className="font-medium text-gray-700 dark:text-gray-300">Filtros</Text>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3 overflow-visible [&>div]:relative [&>div]:overflow-visible">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tipo de Registro</label>
                      <CheckboxMultiSelect
                        options={KIND_FILTER_OPTIONS}
                        selected={dashFilters.kinds ?? []}
                        onChange={kinds => applyDashFilter({ kinds: kinds.length ? kinds as TicketKind[] : undefined })}
                        placeholder="Todos"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Etapa</label>
                      <CheckboxMultiSelect
                        options={stageFilterOptions}
                        selected={dashFilters.stageIds ?? []}
                        onChange={stageIds => applyDashFilter({ stageIds: stageIds.length ? stageIds : undefined })}
                        placeholder="Todas"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Prioridade</label>
                      <CheckboxMultiSelect
                        options={SEVERITY_FILTER_OPTIONS}
                        selected={dashFilters.severities ?? []}
                        onChange={severities => applyDashFilter({ severities: severities.length ? severities as Severity[] : undefined })}
                        placeholder="Todas"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Categoria</label>
                      <CheckboxMultiSelect
                        options={categoryFilterOptions}
                        selected={dashFilters.categoryIds ?? []}
                        onChange={categoryIds => applyDashFilter({ categoryIds: categoryIds.length ? categoryIds : undefined })}
                        placeholder="Todas"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{DEPARTMENT_RESP_LABEL}</label>
                      <CheckboxMultiSelect
                        options={departmentFilterOptions}
                        selected={dashFilters.departmentIds ?? []}
                        onChange={departmentIds => applyDashFilter({ departmentIds: departmentIds.length ? departmentIds : undefined })}
                        placeholder="Todos"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Solicitante</label>
                      <CheckboxMultiSelect
                        options={requesterFilterOptions}
                        selected={dashFilters.requesterUserIds ?? []}
                        onChange={requesterUserIds => applyDashFilter({ requesterUserIds: requesterUserIds.length ? requesterUserIds : undefined })}
                        placeholder="Todos"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Motivo</label>
                      <CheckboxMultiSelect
                        options={ticketTypeFilterOptions}
                        selected={dashFilters.ticketTypeIds ?? []}
                        onChange={ticketTypeIds => applyDashFilter({ ticketTypeIds: ticketTypeIds.length ? ticketTypeIds : undefined })}
                        placeholder="Todos"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Onde</label>
                      <CheckboxMultiSelect
                        options={originFilterOptions}
                        selected={dashFilters.originIds ?? []}
                        onChange={originIds => applyDashFilter({ originIds: originIds.length ? originIds : undefined })}
                        placeholder="Todas"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Corretor</label>
                      <CheckboxMultiSelect
                        options={brokerFilterOptions}
                        selected={dashFilters.brokerIds ?? []}
                        onChange={brokerIds => applyDashFilter({ brokerIds: brokerIds.length ? brokerIds : undefined })}
                        placeholder="Todos"
                        disabled={brokersInTickets.length === 0}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Ocorrência de</label>
                      <input
                        type="date"
                        value={dashFilters.dateFrom || ''}
                        onChange={e => applyDashFilter({ dateFrom: e.target.value || undefined })}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">até</label>
                      <input
                        type="date"
                        value={dashFilters.dateTo || ''}
                        onChange={e => applyDashFilter({ dateTo: e.target.value || undefined })}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Exclusão</label>
                      <CheckboxMultiSelect
                        options={DELETED_FILTER_OPTIONS}
                        selected={dashFilters.deletedFilters ?? []}
                        onChange={deletedFilters => applyDashFilter({
                          deletedFilters: deletedFilters.length
                            ? deletedFilters as Array<'active' | 'deleted'>
                            : undefined,
                          deletedFilter: undefined,
                        })}
                        placeholder="Não excluídos"
                      />
                    </div>
                  </div>
                  {hasDashFilters && (
                    <div className="mt-3 flex justify-end">
                      <Button variant="ghost" onClick={clearDashFilters}>Limpar filtros</Button>
                    </div>
                  )}
                </div>

                {activeTab === 'sla' && (
                  <>
                    {filterLoading || slaCommentsLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <SlaTab
                        incidents={indicatorIncidents}
                        qualityParameters={qualityParameters}
                        firstCommentAtByTicketId={firstCommentAtByTicketId}
                      />
                    )}
                  </>
                )}
                {activeTab === 'max_deadline' && (
                  <>
                    {filterLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <MaxDeadlineTab incidents={indicatorIncidents} />
                    )}
                  </>
                )}
                {activeTab === 'indicators' && (
                  <>
                    {filterLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <IndicatorsTab incidents={indicatorIncidents} stages={stages} />
                    )}
                  </>
                )}
                {activeTab === 'kanban' && (
                  <KanbanTab
                    stages={stages}
                    byStage={byStage}
                    sensors={sensors}
                    activeProfiles={activeProfiles}
                    departmentLabelMap={departmentLabelMap}
                    onDragEnd={onDragEnd}
                    onIncidentCardClick={openKanbanIncidentDetail}
                  />
                )}
                {activeTab === 'matrix' && (
                  <MatrixTab incidents={indicatorIncidents} stages={stages} />
                )}
              </>
            )}
            <IncidentEditModal
              open={editModalOpen && !!editingIncident}
              editing={editingIncident || {}}
              onEditingChange={setEditingIncident}
              stages={stages}
              categories={categories}
              departments={departments}
              activeProfiles={activeProfiles}
              incidentTypes={incidentTypes}
              incidentOrigins={incidentOrigins}
              typeOriginLinks={typeOriginLinks}
              improcedentReasons={improcedentReasons.filter(r => r.is_active)}
              qualityParameters={qualityParameters}
              canUseNotApplicableKind={canUseNotApplicable}
              saving={editSaving}
              onClose={closeEditModal}
              onSave={saveDashboardIncident}
            />

            {detailDrawerIncident && (
              <IncidentDetailDrawer
                incident={detailDrawerIncident}
                stages={stages}
                categories={categories}
                departments={departments}
                activeProfiles={activeProfiles}
                incidentTypes={incidentTypes}
                incidentOrigins={incidentOrigins}
                history={detailHistory}
                historyLoading={detailHistoryLoading}
                onClose={closeDetailDrawer}
                onEdit={openKanbanIncidentEdit}
                onDelete={deleteDashboardIncident}
                editPermissionModuleName={INCIDENTES_MODULE_NAME}
                qualityParameters={qualityParameters}
                improcedentReasons={improcedentReasons}
              />
            )}
          </div>
        </PermissionGuard>
    </div>
  )
}

// ---- Aba SLA ----

const SLA_STATUS_LABELS: Record<SlaFirstResponseStatus, string> = {
  no_sla:           'Sem SLA',
  met:              'No prazo',
  breached:         'Fora do prazo',
  pending_ok:       'Pendente (no prazo)',
  pending_breached: 'Pendente (vencido)',
}

const SLA_STATUS_COLORS: Record<SlaFirstResponseStatus, string> = {
  no_sla:           '#94a3b8',
  met:              '#16a34a',
  breached:         '#dc2626',
  pending_ok:       '#008B95',
  pending_breached: '#ea580c',
}

const SlaTab: React.FC<{
  incidents: QualityIncident[]
  qualityParameters: QualityParameter[]
  firstCommentAtByTicketId: Record<string, string>
}> = ({ incidents, qualityParameters, firstCommentAtByTicketId }) => {
  const { dayStartHour, dayEndHour } = useMemo(
    () => parseQualityBusinessDayWindow(qualityParameters),
    [qualityParameters],
  )

  // Classifica cada ticket
  const classified = useMemo(() => {
    return incidents.map(inc => ({
      inc,
      status: classifySlaPrimeiraResposta(
        inc,
        qualityParameters,
        firstCommentAtByTicketId[inc.id],
      ),
    }))
  }, [incidents, qualityParameters, firstCommentAtByTicketId])

  // KPIs
  const { withSla, met, breached, pendingOk, pendingBreached, avgFirstResponseMs } = useMemo(() => {
    const withSla    = classified.filter(c => c.status !== 'no_sla')
    const met        = classified.filter(c => c.status === 'met').length
    const breached   = classified.filter(c => c.status === 'breached').length
    const pendingOk  = classified.filter(c => c.status === 'pending_ok').length
    const pendingBreached = classified.filter(c => c.status === 'pending_breached').length

    // Tempo médio até 1ª resposta (apenas os que responderam)
    const responded = classified.filter(c => c.status === 'met' || c.status === 'breached')
    let avgFirstResponseMs: number | null = null
    if (responded.length > 0) {
      const total = responded.reduce((acc, { inc }) => {
        const firstIso = firstCommentAtByTicketId[inc.id]
        if (!firstIso) return acc
        return acc + getElapsedBusinessHoursMs(
          new Date(inc.created_at),
          new Date(firstIso),
          dayStartHour,
          dayEndHour,
        )
      }, 0)
      avgFirstResponseMs = total / responded.length
    }

    return { withSla, met, breached, pendingOk, pendingBreached, avgFirstResponseMs }
  }, [classified, firstCommentAtByTicketId, dayStartHour, dayEndHour])

  const withSlaCount    = withSla.length
  const inDayPct        = withSlaCount > 0 ? Math.round(((met + pendingOk) / withSlaCount) * 100) : null
  const respondedCount  = met + breached
  const responseQtyPct  = respondedCount > 0 ? Math.round((met / respondedCount) * 100) : null

  // Gráfico pizza — distribuição de status
  const pieData = useMemo(() => {
    const counts: Record<SlaFirstResponseStatus, number> = {
      no_sla: 0, met: 0, breached: 0, pending_ok: 0, pending_breached: 0,
    }
    classified.forEach(c => { counts[c.status]++ })
    return (Object.keys(counts) as SlaFirstResponseStatus[])
      .filter(k => counts[k] > 0)
      .map(k => ({ name: SLA_STATUS_LABELS[k], value: counts[k], fill: SLA_STATUS_COLORS[k] }))
  }, [classified])

  // Gráfico barras por tipo (kind)
  const byKindData = useMemo(() => {
    const kindMap: Record<string, Record<SlaFirstResponseStatus, number>> = {}
    classified.forEach(({ inc, status }) => {
      const label = kindLabel(inc.kind)
      if (!kindMap[label]) kindMap[label] = { no_sla: 0, met: 0, breached: 0, pending_ok: 0, pending_breached: 0 }
      kindMap[label][status]++
    })
    return Object.entries(kindMap).map(([kind, counts]) => ({
      name: kind,
      'No prazo':       counts.met,
      'Fora do prazo':  counts.breached,
      'Pend. no prazo': counts.pending_ok,
      'Pend. vencido':  counts.pending_breached,
      'Sem SLA':        counts.no_sla,
    }))
  }, [classified])

  // Gráfico barras horizontais por departamento (top 10 por volume com SLA)
  const byDeptData = useMemo(() => {
    const map = new Map<string, { met: number; breached: number; pending_ok: number; pending_breached: number; total: number; fullName: string }>()
    classified
      .filter(c => c.status !== 'no_sla')
      .forEach(({ inc, status }) => {
        const key  = inc.department?.display_name || 'Sem departamento'
        const prev = map.get(key) ?? { met: 0, breached: 0, pending_ok: 0, pending_breached: 0, total: 0, fullName: key }
        prev[status as 'met' | 'breached' | 'pending_ok' | 'pending_breached']++
        prev.total++
        map.set(key, prev)
      })
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map(d => ({
        name:            truncateChartLabel(d.fullName, 22),
        fullName:        d.fullName,
        'No prazo':      d.met,
        'Fora do prazo': d.breached,
        'Pend. no prazo':d.pending_ok,
        'Pend. vencido': d.pending_breached,
      }))
  }, [classified])

  // Série temporal últimos 6 meses
  const monthlyData = useMemo(() => {
    const keys = last6MonthKeys()
    type Bucket = { withSla: number; breached: number; met: number }
    const buckets: Record<string, Bucket> = {}
    keys.forEach(k => { buckets[k] = { withSla: 0, breached: 0, met: 0 } })

    classified.forEach(({ inc, status }) => {
      const mk = monthKey(inc.created_at)
      if (!buckets[mk] || status === 'no_sla') return
      buckets[mk].withSla++
      if (status === 'met')     buckets[mk].met++
      if (status === 'breached') buckets[mk].breached++
    })

    return keys.map(k => ({
      name:            monthLabel(k),
      'Com SLA':       buckets[k].withSla,
      'No prazo':      buckets[k].met,
      'Fora do prazo': buckets[k].breached,
    }))
  }, [classified])

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Com SLA (amostra)</div>
          <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-1">{withSlaCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">% Em dia (operacional)</div>
          <div className="text-3xl font-semibold text-green-600 dark:text-green-400 mt-1">
            {inDayPct !== null ? `${inDayPct}%` : '—'}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">% Respondidos no prazo</div>
          <div className="text-3xl font-semibold text-primary-600 dark:text-primary-400 mt-1">
            {responseQtyPct !== null ? `${responseQtyPct}%` : '—'}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Pendentes vencidos</div>
          <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400 mt-1">{pendingBreached}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Tempo médio 1ª resposta</div>
          <div className="text-3xl font-semibold text-purple-600 dark:text-purple-400 mt-1">
            {avgFirstResponseMs !== null ? formatBusinessHours(avgFirstResponseMs) : '—'}
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
        SLA de <strong>primeira resposta</strong> · seg–sex, janela horária configurada em Parâmetros de Qualidade.
        "Operacional" inclui respondidos no prazo + pendentes ainda no prazo; "Respondidos no prazo" mede só tickets com 1ª resposta registrada.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Distribuição pizza */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Distribuição por status SLA</Text>
          <Text size="xs" color="muted" className="mb-3">Visão geral do portfólio filtrado</Text>
          <div className="h-64 flex items-center justify-center">
            {pieData.length === 0 ? (
              <span className="text-sm text-gray-400">Sem dados</span>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={80}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {pieData.map(entry => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [Number(v), name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Barras por tipo */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">SLA por tipo de ticket</Text>
          <Text size="xs" color="muted" className="mb-3">Incidente vs Melhoria - comparativo de status</Text>
          <div className="h-64">
            {byKindData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byKindData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="No prazo"       stackId="s" fill="#16a34a" />
                  <Bar dataKey="Fora do prazo"  stackId="s" fill="#dc2626" />
                  <Bar dataKey="Pend. no prazo" stackId="s" fill="#008B95" />
                  <Bar dataKey="Pend. vencido"  stackId="s" fill="#ea580c" />
                  <Bar dataKey="Sem SLA"        stackId="s" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Barras horizontais por departamento */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow mb-6">
        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">SLA por {DEPARTMENT_RESP_LABEL} (top 10)</Text>
        <Text size="xs" color="muted" className="mb-3">Tickets com SLA - distribuição de status por departamento responsável</Text>
        <div className="h-72">
          {byDeptData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={byDeptData} margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, name, p) => [Number(v), (p?.payload as { fullName?: string })?.fullName || name]} />
                <Legend />
                <Bar dataKey="No prazo"       stackId="s" fill="#16a34a" />
                <Bar dataKey="Fora do prazo"  stackId="s" fill="#dc2626" />
                <Bar dataKey="Pend. no prazo" stackId="s" fill="#008B95" />
                <Bar dataKey="Pend. vencido"  stackId="s" fill="#ea580c" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Série temporal */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Evolução mensal - SLA (últimos 6 meses)</Text>
        <Text size="xs" color="muted" className="mb-3">
          Por mês de abertura do ticket. "Com SLA" = total com parâmetro configurado; "No prazo" e "Fora do prazo" = apenas respondidos.
        </Text>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="Com SLA"       fill="#94a3b8" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="No prazo"      fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="Fora do prazo" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  )
}

// ---- Aba Prazo Máximo ----

const MAX_DEADLINE_STATUS_LABELS: Record<MaxDeadlineStatus, string> = {
  no_deadline:      'Sem prazo',
  met:              'No prazo',
  breached:         'Fora do prazo',
  pending_ok:       'Pendente (no prazo)',
  pending_breached: 'Pendente (vencido)',
}

const MAX_DEADLINE_STATUS_COLORS: Record<MaxDeadlineStatus, string> = {
  no_deadline:      '#94a3b8',
  met:              '#16a34a',
  breached:         '#dc2626',
  pending_ok:       '#008B95',
  pending_breached: '#ea580c',
}

const MaxDeadlineTab: React.FC<{ incidents: QualityIncident[] }> = ({ incidents }) => {
  const classified = useMemo(() => {
    return incidents.map(inc => ({
      inc,
      status: classifyMaxDeadline(inc),
    }))
  }, [incidents])

  const { withDeadline, met, breached, pendingOk, pendingBreached, avgCloseMs } = useMemo(() => {
    const withDeadline = classified.filter(c => c.status !== 'no_deadline')
    const met = classified.filter(c => c.status === 'met').length
    const breached = classified.filter(c => c.status === 'breached').length
    const pendingOk = classified.filter(c => c.status === 'pending_ok').length
    const pendingBreached = classified.filter(c => c.status === 'pending_breached').length

    const closed = classified.filter(c => c.status === 'met' || c.status === 'breached')
    let avgCloseMs: number | null = null
    if (closed.length > 0) {
      const total = closed.reduce((acc, { inc }) => {
        if (!inc.closed_at) return acc
        return acc + (new Date(inc.closed_at).getTime() - new Date(inc.created_at).getTime())
      }, 0)
      avgCloseMs = total / closed.length
    }

    return { withDeadline, met, breached, pendingOk, pendingBreached, avgCloseMs }
  }, [classified])

  const withDeadlineCount = withDeadline.length
  const inDayPct = withDeadlineCount > 0 ? Math.round(((met + pendingOk) / withDeadlineCount) * 100) : null
  const closedCount = met + breached
  const closedOnTimePct = closedCount > 0 ? Math.round((met / closedCount) * 100) : null

  const pieData = useMemo(() => {
    const counts: Record<MaxDeadlineStatus, number> = {
      no_deadline: 0, met: 0, breached: 0, pending_ok: 0, pending_breached: 0,
    }
    classified.forEach(c => { counts[c.status]++ })
    return (Object.keys(counts) as MaxDeadlineStatus[])
      .filter(k => counts[k] > 0)
      .map(k => ({ name: MAX_DEADLINE_STATUS_LABELS[k], value: counts[k], fill: MAX_DEADLINE_STATUS_COLORS[k] }))
  }, [classified])

  const byKindData = useMemo(() => {
    const kindMap: Record<string, Record<MaxDeadlineStatus, number>> = {}
    classified.forEach(({ inc, status }) => {
      const label = kindLabel(inc.kind)
      if (!kindMap[label]) kindMap[label] = { no_deadline: 0, met: 0, breached: 0, pending_ok: 0, pending_breached: 0 }
      kindMap[label][status]++
    })
    return Object.entries(kindMap).map(([kind, counts]) => ({
      name: kind,
      'No prazo':       counts.met,
      'Fora do prazo':  counts.breached,
      'Pend. no prazo': counts.pending_ok,
      'Pend. vencido':  counts.pending_breached,
      'Sem prazo':      counts.no_deadline,
    }))
  }, [classified])

  const byDeptData = useMemo(() => {
    const map = new Map<string, { met: number; breached: number; pending_ok: number; pending_breached: number; total: number; fullName: string }>()
    classified
      .filter(c => c.status !== 'no_deadline')
      .forEach(({ inc, status }) => {
        const key = inc.department?.display_name || 'Sem departamento'
        const prev = map.get(key) ?? { met: 0, breached: 0, pending_ok: 0, pending_breached: 0, total: 0, fullName: key }
        prev[status as 'met' | 'breached' | 'pending_ok' | 'pending_breached']++
        prev.total++
        map.set(key, prev)
      })
    return Array.from(map.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)
      .map(d => ({
        name:             truncateChartLabel(d.fullName, 22),
        fullName:         d.fullName,
        'No prazo':       d.met,
        'Fora do prazo':  d.breached,
        'Pend. no prazo': d.pending_ok,
        'Pend. vencido':  d.pending_breached,
      }))
  }, [classified])

  const monthlyData = useMemo(() => {
    const keys = last6MonthKeys()
    type Bucket = { withDeadline: number; breached: number; met: number }
    const buckets: Record<string, Bucket> = {}
    keys.forEach(k => { buckets[k] = { withDeadline: 0, breached: 0, met: 0 } })

    classified.forEach(({ inc, status }) => {
      const mk = monthKey(inc.created_at)
      if (!buckets[mk] || status === 'no_deadline') return
      buckets[mk].withDeadline++
      if (status === 'met') buckets[mk].met++
      if (status === 'breached') buckets[mk].breached++
    })

    return keys.map(k => ({
      name:            monthLabel(k),
      'Com prazo':     buckets[k].withDeadline,
      'No prazo':      buckets[k].met,
      'Fora do prazo': buckets[k].breached,
    }))
  }, [classified])

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Com prazo (amostra)</div>
          <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-1">{withDeadlineCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">% Em dia (operacional)</div>
          <div className="text-3xl font-semibold text-green-600 dark:text-green-400 mt-1">
            {inDayPct !== null ? `${inDayPct}%` : '—'}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">% Encerrados no prazo</div>
          <div className="text-3xl font-semibold text-primary-600 dark:text-primary-400 mt-1">
            {closedOnTimePct !== null ? `${closedOnTimePct}%` : '—'}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Pendentes vencidos</div>
          <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400 mt-1">{pendingBreached}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Tempo médio de encerramento</div>
          <div className="text-3xl font-semibold text-purple-600 dark:text-purple-400 mt-1">
            {avgCloseMs !== null ? formatElapsedCalendarTime(avgCloseMs) : '—'}
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
        Prazo máximo corrido · definido manualmente no cadastro do ticket (dias ou horas a partir da abertura).
        &quot;Operacional&quot; inclui encerrados no prazo + pendentes ainda no prazo; &quot;Encerrados no prazo&quot; mede só tickets já fechados.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Distribuição por status</Text>
          <Text size="xs" color="muted" className="mb-3">Visão geral do portfólio filtrado</Text>
          <div className="h-64 flex items-center justify-center">
            {pieData.length === 0 ? (
              <span className="text-sm text-gray-400">Sem dados</span>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={80}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {pieData.map(entry => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [Number(v), name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Prazo máximo por tipo de ticket</Text>
          <Text size="xs" color="muted" className="mb-3">Comparativo de status por tipo de registro</Text>
          <div className="h-64">
            {byKindData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byKindData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 13 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="No prazo"       stackId="s" fill="#16a34a" />
                  <Bar dataKey="Fora do prazo"  stackId="s" fill="#dc2626" />
                  <Bar dataKey="Pend. no prazo" stackId="s" fill="#008B95" />
                  <Bar dataKey="Pend. vencido"  stackId="s" fill="#ea580c" />
                  <Bar dataKey="Sem prazo"      stackId="s" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow mb-6">
        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Prazo máximo por {DEPARTMENT_RESP_LABEL} (top 10)</Text>
        <Text size="xs" color="muted" className="mb-3">Tickets com prazo - distribuição de status por departamento responsável</Text>
        <div className="h-72">
          {byDeptData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={byDeptData} margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, name, p) => [Number(v), (p?.payload as { fullName?: string })?.fullName || name]} />
                <Legend />
                <Bar dataKey="No prazo"       stackId="s" fill="#16a34a" />
                <Bar dataKey="Fora do prazo"  stackId="s" fill="#dc2626" />
                <Bar dataKey="Pend. no prazo" stackId="s" fill="#008B95" />
                <Bar dataKey="Pend. vencido"  stackId="s" fill="#ea580c" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Evolução mensal - Prazo máximo (últimos 6 meses)</Text>
        <Text size="xs" color="muted" className="mb-3">
          Por mês de abertura do ticket. &quot;Com prazo&quot; = total com prazo configurado; &quot;No prazo&quot; e &quot;Fora do prazo&quot; = apenas encerrados.
        </Text>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="Com prazo"     fill="#94a3b8" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="No prazo"      fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="Fora do prazo" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  )
}

// ---- Helpers ----
function monthKey(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function last6MonthKeys(): string[] {
  const keys: string[] = []
  const now = new Date()
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return keys
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-')
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
}

function truncateChartLabel(s: string, maxLen: number): string {
  if (s.length <= maxLen) return s
  return `${s.slice(0, maxLen - 1)}…`
}

const INDICATOR_CATEGORY_AXIS_WIDTH = 220
const INDICATOR_VERTICAL_BAR_MARGIN = { left: 24, right: 24, top: 8, bottom: 8 }

function indicatorChartHeight(itemCount: number): number {
  return Math.max(256, itemCount * 36)
}

const HEATMAP_GREEN: [number, number, number] = [0, 139, 149] // #008b95
const HEATMAP_CELL_ALPHA = 0.58

function heatmapGradientRgb(t: number): [number, number, number] {
  const ratio = 1 - Math.max(0, Math.min(1, t))
  const red: [number, number, number] = [220, 38, 38]
  const yellow: [number, number, number] = [234, 179, 8]

  const lerp = (from: [number, number, number], to: [number, number, number], amount: number) =>
    from.map((channel, index) => Math.round(channel + (to[index] - channel) * amount)) as [number, number, number]

  if (ratio <= 0.5) return lerp(red, yellow, ratio / 0.5)
  return lerp(yellow, HEATMAP_GREEN, (ratio - 0.5) / 0.5)
}

function heatmapCellStyle(value: number, max: number): React.CSSProperties {
  if (value <= 0 || max <= 0) return {}
  const [r, g, b] = heatmapGradientRgb(value / max)
  return { backgroundColor: `rgba(${r}, ${g}, ${b}, ${HEATMAP_CELL_ALPHA})` }
}

function heatmapCellTextClass(value: number, max: number): string {
  if (value <= 0 || max <= 0) return 'text-gray-300 dark:text-gray-600'
  return 'text-gray-900 dark:text-gray-100 font-semibold'
}

const HEATMAP_MATRIX_FIRST_COL = 'w-[7.5rem] min-w-[7.5rem] max-w-[7.5rem] box-border'
const HEATMAP_MATRIX_STICKY_HEAD =
  `sticky left-0 z-20 bg-gray-50 dark:bg-gray-900/30 ${HEATMAP_MATRIX_FIRST_COL}`
const HEATMAP_MATRIX_STICKY_CELL =
  `sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50/80 dark:group-hover:bg-gray-800/50 ${HEATMAP_MATRIX_FIRST_COL}`
const HEATMAP_MATRIX_STICKY_FOOT =
  `sticky left-0 z-10 bg-gray-50 dark:bg-gray-900/30 ${HEATMAP_MATRIX_FIRST_COL}`

const HeatmapMatrixColumnHeader: React.FC<{ label: string }> = ({ label }) => (
  <th className="h-32 p-0 align-bottom text-center min-w-[2.75rem] w-11" title={label}>
    <div className="mx-auto flex h-28 w-full items-end justify-center pb-2">
      <span
        className="max-h-24 overflow-hidden text-ellipsis text-[11px] font-medium uppercase text-gray-500 dark:text-gray-400"
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
      >
        {label}
      </span>
    </div>
  </th>
)

const PIE_COLORS = ['#008B95', '#22c55e', '#eab308', '#f97316', '#a855f7', '#64748b']

type IndicatorChartTab =
  | 'onde'
  | 'prioridade'
  | 'etapa'
  | 'categorias'
  | 'departamento'
  | 'motivo'
  | 'solicitante'
  | 'departamento_solicitante'
  | 'registo_improcedente'

const INDICATOR_CHART_TABS: { id: IndicatorChartTab; label: string }[] = [
  { id: 'onde', label: 'Onde' },
  { id: 'prioridade', label: 'Prioridade' },
  { id: 'etapa', label: 'Etapa' },
  { id: 'categorias', label: 'Categoria' },
  { id: 'departamento', label: 'Departamento' },
  { id: 'motivo', label: 'Motivo' },
  { id: 'solicitante', label: 'Solicitante' },
  { id: 'departamento_solicitante', label: 'Dpto Solicitante' },
  { id: 'registo_improcedente', label: 'Improcedente' },
]

const indicatorChartTabClass = (active: boolean) =>
  `py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
    active
      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
  }`

// ---- Aba Indicadores ----
const IndicatorsTab: React.FC<{ incidents: QualityIncident[]; stages: QualityIncidentStage[] }> = ({
  incidents,
  stages,
}) => {
  const [chartTab, setChartTab] = useState<IndicatorChartTab>('onde')
  const total = incidents.length
  const open = incidents.filter(i => {
    const stage = stages.find(s => s.id === i.stage_id)
    return stage && !stage.is_closed
  }).length
  const high = incidents.filter(i => i.severity === 'high').length
  const closed = incidents.filter(i => {
    const stage = stages.find(s => s.id === i.stage_id)
    return stage?.is_closed
  }).length
  const closureRatePct = total > 0 ? Math.round((closed / total) * 100) : null

  const avgResolutionDays = useMemo(() => {
    const resolved = incidents.filter(i => i.closed_at && i.occurred_at)
    if (resolved.length === 0) return null
    const totalMs = resolved.reduce((acc, i) => {
      return acc + (new Date(i.closed_at!).getTime() - new Date(i.occurred_at!).getTime())
    }, 0)
    return Math.round(totalMs / resolved.length / 86_400_000)
  }, [incidents])

  const bySeverity = useMemo(() => {
    const map: Record<string, number> = { low: 0, medium: 0, high: 0 }
    incidents.forEach(i => { if (i.severity) map[i.severity] = (map[i.severity] || 0) + 1 })
    return Object.entries(map).map(([key, count]) => ({ name: SEVERITY_LABELS[key as Severity], count }))
  }, [incidents])

  const byStageChart = useMemo(() => {
    const map: Record<string, number> = {}
    stages.forEach(s => { map[s.id] = 0 })
    incidents.forEach(i => { map[i.stage_id] = (map[i.stage_id] || 0) + 1 })
    return stages.map(s => ({ name: s.name, count: map[s.id] || 0 }))
  }, [incidents, stages])

  const byCategoryChart = useMemo(() => {
    const m = new Map<string, number>()
    incidents.forEach(i => {
      const label = i.category?.display_name || 'Sem categoria'
      m.set(label, (m.get(label) || 0) + 1)
    })
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [incidents])

  const byDepartmentChart = useMemo(() => {
    const m = new Map<string, number>()
    incidents.forEach(i => {
      const label = i.department?.display_name || `Sem ${DEPARTMENT_RESP_LABEL.toLowerCase()}`
      m.set(label, (m.get(label) || 0) + 1)
    })
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [incidents])

  const byTicketTypeChart = useMemo(() => {
    const m = new Map<string, number>()
    incidents.forEach(i => {
      const label = i.ticket_type?.display_name || 'Sem motivo'
      m.set(label, (m.get(label) || 0) + 1)
    })
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [incidents])

  const openVsClosedPie = useMemo(() => {
    let o = 0
    let c = 0
    incidents.forEach(i => {
      const st = stages.find(s => s.id === i.stage_id)
      if (st?.is_closed) c++
      else o++
    })
    return [
      { name: 'Em aberto', value: o, fill: '#008B95' },
      { name: 'Encerrados', value: c, fill: '#22c55e' },
    ].filter(x => x.value > 0)
  }, [incidents, stages])

  const stageSeverityStack = useMemo(() => {
    return stages.map(s => {
      const list = incidents.filter(i => i.stage_id === s.id)
      return {
        name: s.name,
        Alta: list.filter(i => i.severity === 'high').length,
        Média: list.filter(i => i.severity === 'medium').length,
        Baixa: list.filter(i => i.severity === 'low').length,
        'Sem prioridade': list.filter(i => !i.severity).length,
      }
    })
  }, [incidents, stages])

  const byRequesterDepartmentChart = useMemo(() => {
    const m = new Map<string, number>()
    incidents.forEach(i => {
      const label =
        i.requester?.department?.display_name ||
        i.requester?.department?.name ||
        'Sem departamento solicitante'
      m.set(label, (m.get(label) || 0) + 1)
    })
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [incidents])

  const byRequesterChart = useMemo(() => {
    const m = new Map<string, number>()
    incidents.forEach(i => {
      const label = (i.requester?.name || '').trim() || 'Sem solicitante'
      m.set(label, (m.get(label) || 0) + 1)
    })
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [incidents])

  const byOriginChart = useMemo(() => {
    const m = new Map<string, number>()
    incidents.forEach(i => {
      const label = i.origin?.display_name || 'Vazio'
      m.set(label, (m.get(label) || 0) + 1)
    })
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [incidents])

  const byImprocedentReasonChart = useMemo(() => {
    const m = new Map<string, number>()
    incidents
      .filter(i => i.kind === 'not_applicable')
      .forEach(i => {
        const label = i.improcedent_reason?.display_name || 'Sem registo improcedente'
        m.set(label, (m.get(label) || 0) + 1)
      })
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [incidents])

  const monthlyChart = useMemo(() => {
    const keys = last6MonthKeys()
    const novos: Record<string, number> = {}
    const closedMap: Record<string, number> = {}
    keys.forEach(k => { novos[k] = 0; closedMap[k] = 0 })

    incidents.forEach(i => {
      const mk = monthKey(i.created_at)
      if (novos[mk] !== undefined) novos[mk]++
      if (i.closed_at) {
        const ck = monthKey(i.closed_at)
        if (closedMap[ck] !== undefined) closedMap[ck]++
      }
    })

    let cumNovos = 0
    return keys.map(k => {
      cumNovos += novos[k]
      return {
        name: monthLabel(k),
        Novos: novos[k],
        Encerrados: closedMap[k],
        acumNovos: cumNovos,
      }
    })
  }, [incidents])

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Total de tickets</div>
          <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-1">{total}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Em aberto</div>
          <div className="text-3xl font-semibold text-primary-600 dark:text-primary-400 mt-1">{open}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Prioridade alta</div>
          <div className="text-3xl font-semibold text-red-600 dark:text-red-400 mt-1">{high}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Encerrados</div>
          <div className="text-3xl font-semibold text-green-600 dark:text-green-400 mt-1">{closed}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400">Tempo médio de resolução</div>
          <div className="text-3xl font-semibold text-purple-600 dark:text-purple-400 mt-1">
            {avgResolutionDays !== null ? `${avgResolutionDays}d` : '—'}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 overflow-x-auto">
          <nav className="flex gap-x-4 -mb-px min-w-max">
            {INDICATOR_CHART_TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={indicatorChartTabClass(chartTab === tab.id)}
                onClick={() => setChartTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-5">
          {chartTab === 'onde' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Onde</Text>
              <Text size="xs" color="muted" className="mb-3">Distribuição por origem do ticket</Text>
              <div style={{ height: indicatorChartHeight(byOriginChart.length) }}>
                {byOriginChart.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={byOriginChart} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={INDICATOR_CATEGORY_AXIS_WIDTH}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Tickets" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}

          {chartTab === 'prioridade' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Prioridade</Text>
              <Text size="xs" color="muted" className="mb-3">Distribuição geral do portfólio</Text>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bySeverity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Tickets" fill="#008B95" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {chartTab === 'etapa' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Etapa</Text>
              <Text size="xs" color="muted" className="mb-3">Volume por coluna do fluxo</Text>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byStageChart} margin={{ left: 8, right: 16, bottom: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={72} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Tickets" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {chartTab === 'categorias' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Categoria</Text>
              <Text size="xs" color="muted" className="mb-3">Até 10 categorias com mais tickets</Text>
              <div style={{ height: indicatorChartHeight(byCategoryChart.length) }}>
                {byCategoryChart.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={byCategoryChart} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={INDICATOR_CATEGORY_AXIS_WIDTH}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Tickets" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}

          {chartTab === 'departamento' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Departamento</Text>
              <Text size="xs" color="muted" className="mb-3">Até 10 departamentos com mais tickets</Text>
              <div style={{ height: indicatorChartHeight(byDepartmentChart.length) }}>
                {byDepartmentChart.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={byDepartmentChart} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={INDICATOR_CATEGORY_AXIS_WIDTH}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Tickets" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}

          {chartTab === 'motivo' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Motivo</Text>
              <Text size="xs" color="muted" className="mb-3">Até 10 motivos com mais tickets</Text>
              <div style={{ height: indicatorChartHeight(byTicketTypeChart.length) }}>
                {byTicketTypeChart.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={byTicketTypeChart} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={INDICATOR_CATEGORY_AXIS_WIDTH}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Tickets" fill="#a855f7" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}

          {chartTab === 'solicitante' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Solicitante</Text>
              <Text size="xs" color="muted" className="mb-3">Volume por pessoa solicitante (top 10)</Text>
              <div style={{ height: indicatorChartHeight(byRequesterChart.length) }}>
                {byRequesterChart.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={byRequesterChart} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={INDICATOR_CATEGORY_AXIS_WIDTH}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Tickets" fill="#ec4899" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}

          {chartTab === 'departamento_solicitante' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Dpto Solicitante</Text>
              <Text size="xs" color="muted" className="mb-3">Volume por departamento do cadastro do solicitante (top 10)</Text>
              <div style={{ height: indicatorChartHeight(byRequesterDepartmentChart.length) }}>
                {byRequesterDepartmentChart.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={byRequesterDepartmentChart} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={INDICATOR_CATEGORY_AXIS_WIDTH}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Tickets" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}

          {chartTab === 'registo_improcedente' && (
            <>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Improcedente</Text>
              <Text size="xs" color="muted" className="mb-3">
                Apenas tickets do tipo {kindLabel('not_applicable')} - volume por registo improcedente (top 10)
              </Text>
              <div style={{ height: indicatorChartHeight(byImprocedentReasonChart.length) }}>
                {byImprocedentReasonChart.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-400">Sem dados</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={byImprocedentReasonChart} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={INDICATOR_CATEGORY_AXIS_WIDTH}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar dataKey="count" name="Tickets" fill="#6366f1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Carteira: aberto vs encerrado</Text>
          <Text size="xs" color="muted" className="mb-3">Proporção pela etapa atual (fechada ou não)</Text>
          <div className="h-64 flex items-center justify-center">
            {openVsClosedPie.length === 0 ? (
              <span className="text-sm text-gray-400">Sem dados</span>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={openVsClosedPie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {openVsClosedPie.map((entry, index) => (
                      <Cell key={entry.name} fill={entry.fill || PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Prioridade por etapa</Text>
          <Text size="xs" color="muted" className="mb-3">Empilhado - onde estão riscos altos no fluxo</Text>
          <div style={{ height: indicatorChartHeight(stageSeverityStack.length) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={stageSeverityStack} margin={INDICATOR_VERTICAL_BAR_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={INDICATOR_CATEGORY_AXIS_WIDTH}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Legend />
                <Bar dataKey="Alta" stackId="s" fill="#dc2626" />
                <Bar dataKey="Média" stackId="s" fill="#ca8a04" />
                <Bar dataKey="Baixa" stackId="s" fill="#16a34a" />
                <Bar dataKey="Sem prioridade" stackId="s" fill="#94a3b8" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">Evolução mensal (últimos 6 meses)</Text>
        <Text size="xs" color="muted" className="mb-3">
          Barras: novos registros (data de criação) e encerramentos (data de encerramento). Linha: acumulado de novos.
        </Text>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="Novos" name="Novos no mês" fill="#008B95" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="Encerrados" name="Encerrados no mês" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="acumNovos"
                name="Acumulado novos"
                stroke="#a855f7"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  )
}

// ---- Aba Kanban ----
const KANBAN_COLUMN_WIDTH = 248
const KANBAN_SCROLL_STEP = KANBAN_COLUMN_WIDTH + 12
const KANBAN_VISIBLE_CARDS = 24
const KANBAN_CARD_GAP_PX = 12
const KANBAN_CARD_ESTIMATED_HEIGHT_PX = 92

const handleKanbanColumnWheel = (e: React.WheelEvent<HTMLDivElement>) => {
  e.stopPropagation()
}

const getKanbanColumnListHeight = (cardHeightPx: number) =>
  KANBAN_VISIBLE_CARDS * cardHeightPx + (KANBAN_VISIBLE_CARDS - 1) * KANBAN_CARD_GAP_PX

const KanbanStageHeader: React.FC<{
  stage: QualityIncidentStage
  count: number
  className?: string
}> = ({ stage, count, className = '' }) => {
  const fallback = STAGE_COLORS_FALLBACK[stage.key] || STAGE_COLORS_FALLBACK['open']
  const hasColor = !!stage.color
  const containerStyle = hasColor
    ? { backgroundColor: hexToRgba(stage.color!, 0.08), borderColor: hexToRgba(stage.color!, 0.35) }
    : undefined
  const accentStyle = hasColor ? { backgroundColor: stage.color! } : undefined
  const textStyle = hasColor ? { color: stage.color! } : undefined

  return (
    <div
      className={`flex-shrink-0 px-2.5 pt-3 pb-2.5 border-x border-t border-b-0 ${hasColor ? '' : `${fallback.container} ${fallback.border}`} ${className}`}
      style={{ width: KANBAN_COLUMN_WIDTH, ...containerStyle }}
    >
      <div
        className={`h-1 w-full rounded mb-2.5 ${hasColor ? '' : fallback.accent}`}
        style={accentStyle}
      />
      <div className="flex items-center justify-between gap-2 px-0.5">
        <Text
          size="sm"
          className={`font-semibold truncate ${hasColor ? '' : fallback.headerText}`}
          style={textStyle}
          title={stage.name}
        >
          {stage.name}
        </Text>
        <span
          className={`text-xs flex-shrink-0 ${hasColor ? '' : fallback.headerText}`}
          style={textStyle}
        >
          {count}
        </span>
      </div>
    </div>
  )
}

const KanbanTab: React.FC<{
  stages: QualityIncidentStage[]
  byStage: Record<string, QualityIncident[]>
  sensors: ReturnType<typeof useSensors>
  activeProfiles: QualityActiveProfile[]
  departmentLabelMap: Map<string, string>
  onDragEnd: (event: DragEndEvent) => void
  onIncidentCardClick: (inc: QualityIncident) => void
}> = ({ stages, byStage, sensors, activeProfiles, departmentLabelMap, onDragEnd, onIncidentCardClick }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const cardMeasureRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0)
  const [cardHeightPx, setCardHeightPx] = useState(KANBAN_CARD_ESTIMATED_HEIGHT_PX)

  const columnListHeight = getKanbanColumnListHeight(cardHeightPx)

  const measureCardKey = useMemo(() => {
    for (const stage of stages) {
      const items = byStage[stage.id] || []
      if (items.length > 0) return `${items[0].id}|${stage.id}`
    }
    return null
  }, [stages, byStage])

  const measureCardHeight = useCallback(() => {
    const el = cardMeasureRef.current
    if (!el) return
    const nextHeight = Math.round(el.getBoundingClientRect().height)
    if (nextHeight > 0) {
      setCardHeightPx(prev => (prev === nextHeight ? prev : nextHeight))
    }
  }, [])

  useLayoutEffect(() => {
    measureCardHeight()
    const el = cardMeasureRef.current
    if (!el) return
    const observer = new ResizeObserver(measureCardHeight)
    observer.observe(el)
    return () => observer.disconnect()
  }, [byStage, stages, measureCardHeight])

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setHorizontalScrollLeft(el.scrollLeft)
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    updateScrollState()
    const el = scrollRef.current
    if (!el) return
    const observer = new ResizeObserver(updateScrollState)
    observer.observe(el)
    return () => observer.disconnect()
  }, [stages.length, byStage, updateScrollState])

  const scrollKanbanBy = (dx: number) => {
    scrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }

  const handleKanbanWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      scrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Text size="xs" color="muted" className="hidden sm:block">
            Role horizontalmente ou use as setas para navegar entre as etapas
          </Text>
          <div className="flex items-center gap-1.5 ml-auto">
            <button
              type="button"
              onClick={() => scrollKanbanBy(-KANBAN_SCROLL_STEP)}
              disabled={!canScrollLeft}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              aria-label="Rolar kanban para a esquerda"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollKanbanBy(KANBAN_SCROLL_STEP)}
              disabled={!canScrollRight}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              aria-label="Rolar kanban para a direita"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <div
              className="pointer-events-none absolute left-0 top-[3.25rem] bottom-2 z-10 w-8 bg-gradient-to-r from-gray-100 to-transparent dark:from-gray-900"
              aria-hidden
            />
          )}
          {canScrollRight && (
            <div
              className="pointer-events-none absolute right-0 top-[3.25rem] bottom-2 z-10 w-8 bg-gradient-to-l from-gray-100 to-transparent dark:from-gray-900"
              aria-hidden
            />
          )}

          <div className="sticky top-0 z-20 -mx-1 px-1 bg-gray-100 dark:bg-gray-900">
            <div className="overflow-hidden">
              <div
                className="flex flex-nowrap gap-3 min-w-max pr-1"
                style={{ transform: `translateX(-${horizontalScrollLeft}px)` }}
              >
                {stages.map(stage => (
                  <KanbanStageHeader
                    key={`header-${stage.id}`}
                    stage={stage}
                    count={(byStage[stage.id] || []).length}
                    className="rounded-t-lg rounded-b-none"
                  />
                ))}
              </div>
            </div>
          </div>

          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            onWheel={handleKanbanWheel}
            className="overflow-x-auto overscroll-x-contain pb-2 scrollbar-thin snap-x snap-mandatory"
          >
            <div className="flex flex-nowrap items-start gap-3 min-w-max pr-1">
              {stages.map(stage => {
                const fallback = STAGE_COLORS_FALLBACK[stage.key] || STAGE_COLORS_FALLBACK['open']
                const hasColor = !!stage.color

                const containerStyle = hasColor
                  ? { backgroundColor: hexToRgba(stage.color!, 0.08), borderColor: hexToRgba(stage.color!, 0.35) }
                  : undefined

                return (
                  <div
                    key={stage.id}
                    className={`flex-shrink-0 snap-start flex flex-col rounded-b-lg px-2.5 pt-3 pb-3 border-x border-b border-t-0 ${hasColor ? '' : `${fallback.container} ${fallback.border}`}`}
                    style={{ width: KANBAN_COLUMN_WIDTH, ...containerStyle }}
                  >
                    <SortableContext
                      items={(byStage[stage.id] || []).map(i => `${i.id}|${stage.id}`)}
                      strategy={rectSortingStrategy}
                    >
                      <div
                        id={stage.id}
                        onWheel={handleKanbanColumnWheel}
                        className="space-y-3 overflow-y-auto overscroll-y-contain scrollbar-thin pr-1 -mr-1"
                        style={{
                          height: columnListHeight,
                          minHeight: 120,
                        }}
                      >
                        {(byStage[stage.id] || []).map(inc => (
                          <div
                            key={`${inc.id}|${stage.id}`}
                            id={`${inc.id}|${stage.id}`}
                            ref={`${inc.id}|${stage.id}` === measureCardKey ? cardMeasureRef : undefined}
                            role="button"
                            tabIndex={0}
                            onClick={() => onIncidentCardClick(inc)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onIncidentCardClick(inc)
                              }
                            }}
                            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200/90 dark:border-gray-700/90 shadow-sm px-3 py-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/40 dark:hover:bg-primary-950/30 hover:shadow-lg hover:ring-2 hover:ring-primary-500/70 dark:hover:ring-primary-400/80 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <div className="flex flex-col gap-1.5">
                              <div className="space-y-0.5">
                                <div className="text-[11px] font-mono leading-none text-gray-500 dark:text-gray-400">
                                  {formatTicketNumber(inc.ticket_number)}
                                </div>
                                <div
                                  className="text-sm text-gray-900 dark:text-white font-medium leading-snug line-clamp-2"
                                  title={inc.title}
                                >
                                  {inc.title}
                                </div>
                              </div>

                              <div className="text-xs leading-normal text-gray-500 dark:text-gray-400 space-y-0.5">
                                <div className="break-words">
                                  <span className="text-gray-400 dark:text-gray-500">{DEPARTMENT_RESP_LABEL}: </span>
                                  {inc.department?.display_name || '—'}
                                </div>
                                <div className="break-words">
                                  <span className="text-gray-400 dark:text-gray-500">Solicitante: </span>
                                  {formatRequesterDisplay(inc.requester, activeProfiles, departmentLabelMap, inc.requester_user_id)}
                                </div>
                              </div>

                              {inc.severity && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_COLORS[inc.severity]}`}>
                                    {SEVERITY_LABELS[inc.severity]}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </SortableContext>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </DndContext>
  )
}

// ---- Aba Matriz (cruzamento configurável) ----

type MatrixDimension =
  | 'categoria'
  | 'departamento'
  | 'onde'
  | 'motivo'
  | 'prioridade'
  | 'etapa'
  | 'solicitante'
  | 'departamento_solicitante'
  | 'tipo'
  | 'registo_improcedente'

type MatrixAxisItem = { key: string; label: string }

const MATRIX_DIMENSION_OPTIONS: { id: MatrixDimension; label: string }[] = [
  { id: 'categoria', label: 'Categoria' },
  { id: 'departamento', label: DEPARTMENT_RESP_LABEL },
  { id: 'onde', label: 'Onde' },
  { id: 'motivo', label: 'Motivo' },
  { id: 'prioridade', label: 'Prioridade' },
  { id: 'etapa', label: 'Etapa' },
  { id: 'solicitante', label: 'Solicitante' },
  { id: 'departamento_solicitante', label: 'Dpto solicitante' },
  { id: 'tipo', label: 'Tipo' },
  { id: 'registo_improcedente', label: 'Registo improcedente' },
]

function matrixDimensionLabel(id: MatrixDimension): string {
  return MATRIX_DIMENSION_OPTIONS.find(o => o.id === id)?.label ?? id
}

function getIncidentMatrixAxisValue(
  inc: QualityIncident,
  dimension: MatrixDimension,
  stages: QualityIncidentStage[],
): MatrixAxisItem {
  switch (dimension) {
    case 'categoria':
      return {
        key: inc.category_id || '__none__',
        label: inc.category?.display_name || 'Sem categoria',
      }
    case 'departamento':
      return {
        key: inc.department_id || '__none__',
        label: inc.department?.display_name || `Sem ${DEPARTMENT_RESP_LABEL.toLowerCase()}`,
      }
    case 'onde':
      return {
        key: inc.origin_id || '__none__',
        label: inc.origin?.display_name || 'Vazio',
      }
    case 'motivo':
      return {
        key: inc.ticket_type_id || '__none__',
        label: inc.ticket_type?.display_name || 'Sem motivo',
      }
    case 'prioridade':
      return inc.severity
        ? { key: inc.severity, label: SEVERITY_LABELS[inc.severity] }
        : { key: '__none__', label: 'Sem prioridade' }
    case 'etapa': {
      const stage = stages.find(s => s.id === inc.stage_id)
      return {
        key: inc.stage_id || '__none__',
        label: stage?.name || 'Sem etapa',
      }
    }
    case 'solicitante':
      return {
        key: inc.requester_user_id || '__none__',
        label: (inc.requester?.name || '').trim() || 'Sem solicitante',
      }
    case 'departamento_solicitante':
      return {
        key: inc.requester?.department?.id || '__none__',
        label:
          inc.requester?.department?.display_name ||
          inc.requester?.department?.name ||
          'Sem departamento solicitante',
      }
    case 'tipo':
      return { key: inc.kind, label: kindLabel(inc.kind) }
    case 'registo_improcedente':
      return {
        key: inc.improcedent_reason_id || '__none__',
        label: inc.improcedent_reason?.display_name || 'Sem registo improcedente',
      }
  }
}

function buildMatrixAxes(
  incidents: QualityIncident[],
  dimension: MatrixDimension,
  stages: QualityIncidentStage[],
): MatrixAxisItem[] {
  const map = new Map<string, string>()
  incidents.forEach(inc => {
    const { key, label } = getIncidentMatrixAxisValue(inc, dimension, stages)
    if (!map.has(key)) map.set(key, label)
  })
  return Array.from(map.entries()).map(([key, label]) => ({ key, label }))
}

function sortMatrixAxisByTotal(
  items: MatrixAxisItem[],
  matrix: Record<string, Record<string, number>>,
  axis: 'row' | 'col',
  otherKeys: string[],
): MatrixAxisItem[] {
  return [...items].sort((a, b) => {
    const totalA =
      axis === 'row'
        ? otherKeys.reduce((acc, colKey) => acc + (matrix[a.key]?.[colKey] || 0), 0)
        : otherKeys.reduce((acc, rowKey) => acc + (matrix[rowKey]?.[a.key] || 0), 0)
    const totalB =
      axis === 'row'
        ? otherKeys.reduce((acc, colKey) => acc + (matrix[b.key]?.[colKey] || 0), 0)
        : otherKeys.reduce((acc, rowKey) => acc + (matrix[rowKey]?.[b.key] || 0), 0)
    return totalB - totalA || a.label.localeCompare(b.label, 'pt-BR')
  })
}

const MatrixTab: React.FC<{ incidents: QualityIncident[]; stages: QualityIncidentStage[] }> = ({
  incidents,
  stages,
}) => {
  const [rowDimension, setRowDimension] = useState<MatrixDimension>('categoria')
  const [colDimension, setColDimension] = useState<MatrixDimension>('departamento')

  const sameDimension = rowDimension === colDimension

  const { rows, cols, matrix, maxCell } = useMemo(() => {
    if (sameDimension) {
      return {
        rows: [] as MatrixAxisItem[],
        cols: [] as MatrixAxisItem[],
        matrix: {} as Record<string, Record<string, number>>,
        maxCell: 0,
      }
    }

    const rowItems = buildMatrixAxes(incidents, rowDimension, stages)
    const colItems = buildMatrixAxes(incidents, colDimension, stages)
    const mx: Record<string, Record<string, number>> = {}
    let max = 0

    incidents.forEach(inc => {
      const row = getIncidentMatrixAxisValue(inc, rowDimension, stages)
      const col = getIncidentMatrixAxisValue(inc, colDimension, stages)
      if (!mx[row.key]) mx[row.key] = {}
      const next = (mx[row.key][col.key] || 0) + 1
      mx[row.key][col.key] = next
      if (next > max) max = next
    })

    const colKeys = colItems.map(c => c.key)
    const rowKeys = rowItems.map(r => r.key)
    const sortedRows = sortMatrixAxisByTotal(rowItems, mx, 'row', colKeys)
    const sortedCols = sortMatrixAxisByTotal(colItems, mx, 'col', rowKeys)

    return { rows: sortedRows, cols: sortedCols, matrix: mx, maxCell: max }
  }, [incidents, rowDimension, colDimension, stages, sameDimension])

  const grandTotal = useMemo(() => {
    return rows.reduce((acc, row) => {
      return acc + cols.reduce((rowAcc, col) => rowAcc + (matrix[row.key]?.[col.key] || 0), 0)
    }, 0)
  }, [rows, cols, matrix])

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <Text size="sm" color="muted">
          Cruzamento de tickets - selecione as dimensões das linhas e colunas
        </Text>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Linhas</label>
            <Select
              value={rowDimension}
              onChange={e => setRowDimension(e.target.value as MatrixDimension)}
            >
              {MATRIX_DIMENSION_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Colunas</label>
            <Select
              value={colDimension}
              onChange={e => setColDimension(e.target.value as MatrixDimension)}
            >
              {MATRIX_DIMENSION_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {incidents.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
          Nenhum ticket encontrado.
        </div>
      ) : sameDimension ? (
        <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
          Selecione dimensões diferentes para linhas e colunas.
        </div>
      ) : (
        <>
          <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">
            {matrixDimensionLabel(rowDimension)} × {matrixDimensionLabel(colDimension)}
          </Text>
          <Text size="xs" color="muted" className="mb-3">
            Escala verde → amarelo → vermelho conforme o volume - linhas e colunas ordenadas do maior para o menor
          </Text>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 dark:border-gray-700 rounded-lg">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <th className={`px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200 dark:border-gray-700 truncate ${HEATMAP_MATRIX_STICKY_HEAD}`}>
                    {matrixDimensionLabel(rowDimension)} \ {matrixDimensionLabel(colDimension)}
                  </th>
                  {cols.map(col => (
                    <HeatmapMatrixColumnHeader key={col.key} label={col.label} />
                  ))}
                  <HeatmapMatrixColumnHeader label="Total" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map(row => {
                  const rowTotal = cols.reduce((acc, col) => acc + (matrix[row.key]?.[col.key] || 0), 0)
                  return (
                    <tr key={row.key} className="group hover:bg-gray-50/80 dark:hover:bg-gray-800/50">
                      <td
                        className={`px-2 py-2 text-sm text-gray-900 dark:text-white font-medium border-r border-gray-200 dark:border-gray-700 truncate ${HEATMAP_MATRIX_STICKY_CELL}`}
                        title={row.label}
                      >
                        {truncateChartLabel(row.label, 16)}
                      </td>
                      {cols.map(col => {
                        const count = matrix[row.key]?.[col.key] || 0
                        return (
                          <td
                            key={col.key}
                            className="px-3 py-2 text-center text-sm"
                            style={heatmapCellStyle(count, maxCell)}
                            title={`${row.label} · ${col.label}: ${count} ticket(s)`}
                          >
                            {count > 0 ? (
                              <span className={heatmapCellTextClass(count, maxCell)}>{count}</span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                        )
                      })}
                      <td className="px-3 py-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {rowTotal}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <td className={`px-2 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 ${HEATMAP_MATRIX_STICKY_FOOT}`}>
                    Total
                  </td>
                  {cols.map(col => {
                    const colTotal = rows.reduce((acc, row) => acc + (matrix[row.key]?.[col.key] || 0), 0)
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-300"
                      >
                        {colTotal > 0 ? colTotal : '—'}
                      </td>
                    )
                  })}
                  <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 dark:text-white">
                    {grandTotal}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

export default QualidadeDashboard
